"use server";

import { cookies, headers } from "next/headers";
import { attr, BOX_ATTR, giftBoxToCartLines, MAX_MESSAGE_LENGTH, newBoxId } from "@/lib/box";
import { addCartLines, createCart, getBox, getCart, removeCartLines, updateCartLines } from "@/lib/shopify";
import type { Cart, CartLineInput, CartUpdate } from "@/lib/shopify/types";
import { CART_COOKIE, cartCookieOptions } from "@/lib/cart-cookie";
import { site } from "@/lib/site";

// `notice` is set when Shopify changed what was asked for, for example
// because there wasn't enough stock. A failure can still carry the cart when
// it changed on the way.
export type CartResult =
  | { ok: true; cart: Cart | null; notice?: string }
  | { ok: false; error: string; cart?: Cart | null };

const MAX_LINES = 40;
const MAX_QUANTITY = 50;

// Server actions are public endpoints, so nothing from the client is trusted.
function parseLines(input: unknown): CartLineInput[] | null {
  if (!Array.isArray(input) || input.length === 0 || input.length > MAX_LINES) return null;
  const lines: CartLineInput[] = [];
  for (const raw of input) {
    if (typeof raw !== "object" || raw === null) return null;
    const { merchandiseId, quantity, attributes } = raw as Record<string, unknown>;
    if (typeof merchandiseId !== "string" || !merchandiseId.startsWith("gid://") || merchandiseId.length > 200) return null;
    if (typeof quantity !== "number" || !Number.isInteger(quantity) || quantity < 1 || quantity > MAX_QUANTITY) return null;
    const parsed: { key: string; value: string }[] = [];
    if (attributes !== undefined) {
      if (!Array.isArray(attributes) || attributes.length > 10) return null;
      for (const a of attributes) {
        if (typeof a?.key !== "string" || typeof a?.value !== "string") return null;
        if (!a.key || a.key.length > 40 || a.value.length > 500) return null;
        if (a.value) parsed.push({ key: a.key, value: a.value });
      }
    }
    lines.push({ merchandiseId, quantity, attributes: parsed });
  }
  return lines;
}

const isId = (value: unknown): value is string => typeof value === "string" && value.length > 0 && value.length < 300;

async function buyerIp(): Promise<string | undefined> {
  return (await headers()).get("x-forwarded-for")?.split(",")[0]?.trim() || undefined;
}

function failure(error: unknown): CartResult {
  console.error("[cart]", error);
  const message = error instanceof Error && error.name !== "ShopifyError" ? error.message : null;
  return { ok: false, error: message ?? "We couldn't update your cart. Check your connection and try again." };
}

async function addToCart(lines: CartLineInput[], ip: string | undefined): Promise<CartUpdate | null> {
  const jar = await cookies();
  const cartId = jar.get(CART_COOKIE)?.value;
  // A checked-out or expired cart comes back null, so start a fresh one.
  const existing = cartId ? await getCart(cartId, ip) : null;
  const update = existing ? await addCartLines(existing.id, lines, ip) : await createCart(lines, ip);
  if (update) jar.set(CART_COOKIE, update.cart.id, cartCookieOptions);
  return update;
}

// Takes out the lines Shopify kept at quantity 0 because they sold out, and
// passes its stock warnings on to the shopper.
async function settle(update: CartUpdate, ip: string | undefined): Promise<{ cart: Cart; notice?: string }> {
  const present = new Set(update.cart.lines.map((line) => line.id));
  const empty = update.warnings.flatMap((w) => (w.lineId && !present.has(w.lineId) ? [w.lineId] : []));
  const cart = empty.length ? ((await removeCartLines(update.cart.id, empty, ip)) ?? update.cart) : update.cart;
  const notice = [...new Set(update.warnings.map((w) => w.message))].join(" ");
  return notice ? { cart, notice } : { cart };
}

export async function addLinesAction(input: unknown): Promise<CartResult> {
  const lines = parseLines(input);
  if (!lines) return { ok: false, error: "That doesn't look like something we can add to a cart." };

  try {
    const ip = await buyerIp();
    const update = await addToCart(lines, ip);
    return update ? { ok: true, ...(await settle(update, ip)) } : { ok: true, cart: null };
  } catch (error) {
    return failure(error);
  }
}

// Adds one of our gift boxes. The contents are looked up here rather than
// taken from the browser, and a box that can't go in whole doesn't go in at all.
export async function addBoxAction(handle: unknown, message: unknown): Promise<CartResult> {
  if (typeof handle !== "string" || !handle || handle.length > 255) return { ok: false, error: "That box couldn't be found." };
  if (message !== undefined && (typeof message !== "string" || message.length > MAX_MESSAGE_LENGTH)) {
    return { ok: false, error: `A note can be up to ${MAX_MESSAGE_LENGTH} characters long.` };
  }

  try {
    const box = await getBox(handle);
    if (!box) return { ok: false, error: "That box isn't in the shop any more." };
    if (!box.availableForSale) return { ok: false, error: `Sorry, ${box.title} has sold out.` };

    const ip = await buyerIp();
    const boxId = newBoxId();
    const lines = giftBoxToCartLines(box, message, boxId);
    const update = await addToCart(lines, ip);
    if (!update) return failure(new Error("The cart disappeared while the box was being added."));

    const added = update.cart.lines.filter((line) => attr(line.attributes, BOX_ATTR.id) === boxId);
    const missing = lines.filter(
      (line) => !added.some((a) => a.merchandise.id === line.merchandiseId && a.quantity >= line.quantity),
    );
    if (!missing.length) return { ok: true, cart: update.cart };

    const leftovers = new Set([...added.map((line) => line.id), ...update.warnings.flatMap((w) => (w.lineId ? [w.lineId] : []))]);
    const cart = (await removeCartLines(update.cart.id, [...leftovers], ip)) ?? update.cart;
    const soldOut = box.items
      .filter((item) => missing.some((line) => line.merchandiseId === item.variant?.id))
      .map((item) => item.product.title);
    const what =
      soldOut.length > 3
        ? `${soldOut.length} things in it`
        : new Intl.ListFormat(site.locale, { type: "conjunction" }).format(soldOut);
    return {
      ok: false,
      error: `Sorry, ${what} ${soldOut.length === 1 ? "has" : "have"} just sold out, so we couldn't add ${box.title}.`,
      cart,
    };
  } catch (error) {
    return failure(error);
  }
}

export async function updateLineAction(lineId: unknown, quantity: unknown): Promise<CartResult> {
  if (!isId(lineId) || typeof quantity !== "number" || !Number.isInteger(quantity) || quantity < 0 || quantity > MAX_QUANTITY) {
    return { ok: false, error: "That quantity isn't valid." };
  }
  try {
    const cartId = (await cookies()).get(CART_COOKIE)?.value;
    if (!cartId) return { ok: true, cart: null };
    const ip = await buyerIp();
    if (quantity === 0) return { ok: true, cart: await removeCartLines(cartId, [lineId], ip) };
    const update = await updateCartLines(cartId, [{ id: lineId, quantity }], ip);
    return update ? { ok: true, ...(await settle(update, ip)) } : { ok: true, cart: null };
  } catch (error) {
    return failure(error);
  }
}

export async function removeLinesAction(lineIds: unknown): Promise<CartResult> {
  // A whole box can be removed at once, so this allows as many lines as a cart holds.
  if (!Array.isArray(lineIds) || lineIds.length === 0 || lineIds.length > 250 || !lineIds.every(isId)) {
    return { ok: false, error: "Those items couldn't be removed." };
  }
  try {
    const cartId = (await cookies()).get(CART_COOKIE)?.value;
    if (!cartId) return { ok: true, cart: null };
    return { ok: true, cart: await removeCartLines(cartId, lineIds, await buyerIp()) };
  } catch (error) {
    return failure(error);
  }
}
